import { isWrapped } from '@opentelemetry/instrumentation';
import {
  consoleSandbox,
  getClient,
  getOriginalFunction,
  getGlobalScope,
  hasSpansEnabled,
  isEnabled,
  type WrappedFunction,
} from '@sentry/core';
import type { NodeClient } from '../sdk/client';
import { createMissingInstrumentationContext } from './createMissingInstrumentationContext';

/**
 * Checks and warns if a framework isn't wrapped by opentelemetry.
 */
export function ensureIsWrapped(
  maybeWrappedFunction: unknown,
  name: 'express' | 'connect' | 'fastify' | 'hapi' | 'koa' | 'hono',
): void {
  const clientOptions = getClient<NodeClient>()?.getOptions();
  if (
    !clientOptions?.disableInstrumentationWarnings &&
    !(
      isWrapped(maybeWrappedFunction) ||
      typeof getOriginalFunction(maybeWrappedFunction as WrappedFunction) === 'function'
    ) &&
    isEnabled() &&
    hasSpansEnabled(clientOptions)
  ) {
    consoleSandbox(() => {
      /*! rollup-include-cjs-only */
      // eslint-disable-next-line no-console
      console.warn(
        `[Sentry] ${name} is not instrumented. This is likely because you required/imported ${name} before calling \`Sentry.init()\`.`,
      );
      /*! rollup-include-cjs-only-end */

      /*! rollup-include-esm-only */
      // eslint-disable-next-line no-console
      console.warn(
        `[Sentry] ${name} is not instrumented. Please make sure to initialize Sentry in a separate file that you \`--import\` when running node, see: https://docs.sentry.io/platforms/javascript/guides/${name}/install/esm/.`,
      );
      /*! rollup-include-esm-only-end */
    });

    getGlobalScope().setContext('missing_instrumentation', createMissingInstrumentationContext(name));
  }
}
